import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  LayoutDashboard, BookOpen, Trophy, Award, Users, Building2,
  Bell, Settings as SettingsIcon, Headphones, Sparkles, ShieldCheck,
  FileBadge, Newspaper, Calendar, Menu, X,
} from "lucide-react";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";
import { usePublicConfig } from "../lib/publicConfig";
import ContactSupportSheet from "./ContactSupportSheet";
import OrgSwitcher from "./OrgSwitcher";

const ITEMS = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "side-dashboard" },
  { to: "/calendar", label: "Calendar", icon: Calendar, testid: "side-calendar" },
  { to: "/my-courses", label: "My Courses", icon: BookOpen, testid: "side-my-courses" },
  { to: "/org", label: "Organization", icon: Building2, testid: "side-org" },
  { to: "/community", label: "Community", icon: Users, testid: "side-community" },
  { to: "/internal", label: "Team Feed", icon: Building2, testid: "side-internal" },
  { to: "/newsletter", label: "Freddy Media News", icon: Newspaper, testid: "side-newsletter" },
  { to: "/notifications", label: "Notifications", icon: Bell, testid: "side-notifications" },
  { to: "/leaderboard", label: "Leaderboard", icon: Trophy, testid: "side-leaderboard" },
  { to: "/achievements", label: "Achievements", icon: Award, testid: "side-achievements" },
  { to: "/certificates", label: "Certificates", icon: ShieldCheck, testid: "side-certificates" },
  { to: "/settings", label: "Settings", icon: SettingsIcon, testid: "side-settings" },
];

const ADMIN_ITEMS = [
  { to: "/admin/assessments", label: "Assessments", icon: Sparkles, testid: "side-admin-assessments" },
  { to: "/admin/certificates", label: "Certificates", icon: FileBadge, testid: "side-admin-certificates" },
  { to: "/admin/support", label: "Support Tickets", icon: Headphones, testid: "side-admin-support" },
];

export default function Sidebar() {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const cfg = usePublicConfig();
  const [supportOpen, setSupportOpen] = useState(false);
  const [buildUrl, setBuildUrl] = useState("https://freddycmpute.com/talk");
  const [unread, setUnread] = useState(0);
  // Phase 18 — mobile drawer state. Sidebar was `hidden lg:flex` with no fallback,
  // leaving users below 1024px with no nav. Off-canvas drawer + top bar fix that.
  const [mobileOpen, setMobileOpen] = useState(false);
  useEffect(() => { setMobileOpen(false); }, [pathname]);

  // Phase 13.4 — hide user-facing /certificates entry when the feature flag is off.
  // Phase 17 — same treatment for /leaderboard + /achievements when the leaderboards
  // kill-switch is off (achievements shares the ranking UX so it's gated together).
  const visibleItems = ITEMS.filter((it) => {
    if (it.to === "/certificates" && !cfg.certificates_enabled) return false;
    if ((it.to === "/leaderboard" || it.to === "/achievements") && !cfg.leaderboards_enabled) return false;
    // Phase 20 — hide public community when the kill-switch is off.
    if (it.to === "/community" && !cfg.community_enabled) return false;
    return true;
  });

  useEffect(() => {
    if (!user) return;
    api.get("/settings/support-links").then(r => {
      if (r.data?.build_url) setBuildUrl(r.data.build_url);
    }).catch(() => {});
    api.get("/notifications/me").then(r => setUnread(r.data?.unread_count || 0)).catch(() => {});
  }, [user]);

  return (
    <>
      {/* Phase 18 — mobile top bar (visible below lg). Fixed position so scrollable
          pages don't lose it. Renders on top of content. */}
      <header className="lg:hidden fixed top-0 left-0 right-0 z-40 h-14 bg-[#060A24] border-b border-white/10 flex items-center justify-between px-4" data-testid="mobile-topbar">
        <Link to="/" className="flex items-center gap-2">
          <img src="/brand/freddy-symbol.webp" alt="Freddy" className="h-7 w-auto" />
          <span className="text-white font-black tracking-[0.14em] text-[10px] leading-tight">FREDDY<br/>COMPUTE</span>
        </Link>
        <button
          onClick={() => setMobileOpen(true)}
          className="w-11 h-11 flex items-center justify-center text-white/85 hover:text-white"
          aria-label="Open menu"
          data-testid="mobile-menu-button"
        >
          <Menu size={22} />
        </button>
      </header>
      {/* Spacer so page content clears the fixed top bar on mobile */}
      <div className="lg:hidden h-14 flex-shrink-0" aria-hidden />

      {/* Mobile drawer backdrop */}
      {mobileOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/60 z-40"
          onClick={() => setMobileOpen(false)}
          aria-hidden
        />
      )}

      <aside
        className={`
          bg-[#060A24] border-r border-white/10 flex flex-col
          lg:sticky lg:top-0 lg:flex lg:w-64 lg:flex-shrink-0 lg:h-screen
          fixed inset-y-0 left-0 z-50 w-72 max-w-[85vw] transition-transform duration-200
          ${mobileOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}
        `}
        data-testid="dashboard-sidebar"
      >
        <div className="flex items-center gap-3 h-20 px-6 border-b border-white/10">
          <Link to="/" className="flex items-center gap-3 flex-1">
            <img src="/brand/freddy-symbol.webp" alt="Freddy" className="h-9 w-auto" />
            <span className="text-white font-black tracking-[0.18em] text-[11px] leading-tight">
              FREDDY<br/>COMPUTE
            </span>
          </Link>
          <button
            className="lg:hidden w-9 h-9 flex items-center justify-center text-white/70 hover:text-white"
            onClick={() => setMobileOpen(false)}
            aria-label="Close menu"
            data-testid="mobile-menu-close"
          >
            <X size={18} />
          </button>
        </div>

        <nav className="flex-1 px-3 py-6 overflow-y-auto space-y-1 no-scrollbar">
          {user && (
            <div className="mb-4">
              <OrgSwitcher />
            </div>
          )}
          {visibleItems.map((it) => {
            const active = pathname === it.to || pathname.startsWith(it.to + "/");
            const Icon = it.icon;
            const showBadge = it.to === "/notifications" && unread > 0;
            return (
              <Link
                key={it.to}
                to={it.to}
                className={`flex items-center justify-between gap-3 px-3 py-2.5 text-sm font-semibold rounded-sm transition-colors ${
                  active ? "bg-fc-red/15 text-fc-red border-l-2 border-fc-red" : "text-white/80 hover:bg-white/5 hover:text-white"
                }`}
                data-testid={it.testid}
              >
                <span className="flex items-center gap-3"><Icon size={16} /> {it.label}</span>
                {showBadge && (
                  <span className="bg-fc-red text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full min-w-[18px] text-center">{unread}</span>
                )}
              </Link>
            );
          })}

          {user?.role === "super_admin" && (
            <>
              <div className="px-3 mt-6 mb-2 text-[10px] tracking-[0.22em] uppercase font-bold text-white/40">Admin</div>
              {ADMIN_ITEMS.map((it) => {
                const Icon = it.icon;
                const active = pathname === it.to;
                return (
                  <Link
                    key={it.to}
                    to={it.to}
                    className={`flex items-center gap-3 px-3 py-2.5 text-sm font-semibold rounded-sm transition-colors ${
                      active ? "bg-fc-red/15 text-fc-red border-l-2 border-fc-red" : "text-white/80 hover:bg-white/5 hover:text-white"
                    }`}
                    data-testid={it.testid}
                  >
                    <Icon size={16} /> {it.label}
                  </Link>
                );
              })}
            </>
          )}
        </nav>

        <div className="px-3 py-4 border-t border-white/10 space-y-1">
          <button
            onClick={() => setSupportOpen(true)}
            className="w-full flex items-center gap-3 px-3 py-2.5 text-white/80 hover:bg-white/5 hover:text-white text-sm font-semibold rounded-sm transition-colors"
            data-testid="side-support"
          >
            <Headphones size={16} /> Contact Support
          </button>
          <Link
            to="/talk-to-us"
            className="w-full flex items-center gap-3 px-3 py-2.5 text-white/80 hover:bg-fc-red/10 hover:text-fc-red text-sm font-semibold rounded-sm transition-colors"
            data-testid="side-talk-to-us"
          >
            <Sparkles size={16} /> Talk to us
          </Link>
        </div>
      </aside>

      <ContactSupportSheet open={supportOpen} onOpenChange={setSupportOpen} />
    </>
  );
}
